import type {
  CanonicalCompatibilityEvidence,
} from '../tree/CanonicalTreeCompatibilityEdgeProjection';

import type {
  CanonicalTreeRelationshipEdge,
} from '../tree/CanonicalTreeRelationshipEdge';

import type {
  CanonicalTreeTopologyReplacementMemberResult,
} from '../tree/CanonicalTreeTopologyReplacementSafetyGate';

import type {
  CanonicalViewerMemberProjection,
} from '../tree/CanonicalTreeViewerProjection';

export const TREE_RUNTIME_SELECTOR_DISCREPANCY_SOURCE_KEYS =
  Object.freeze([
    'canonicalAdditionCanonicalEdgeCount',
    'canonicalAdditionRelationshipHistoryCount',
    'canonicalAdditionSpousePartnerEdgesCount',
    'canonicalAdditionRelationshipWorkflowCount',
    'canonicalAdditionSpouseIdCount',
    'canonicalAdditionParentIdsCount',
    'canonicalAdditionNoTerminalEdgeCount',
    'canonicalAdditionUnknownEdgeSourceCount',

    'legacyGenerationMissingCanonicalEdgeCount',
    'legacyGenerationMissingRelationshipHistoryCount',
    'legacyGenerationMissingSpousePartnerEdgesCount',
    'legacyGenerationMissingRelationshipWorkflowCount',
    'legacyGenerationMissingSpouseIdCount',
    'legacyGenerationMissingParentIdsCount',
    'legacyGenerationMissingNoTerminalEdgeCount',
    'legacyGenerationMissingUnknownEdgeSourceCount',

    'missingReferenceCanonicalEdgeCount',
    'missingReferenceRelationshipHistoryCount',
    'missingReferenceSpousePartnerEdgesCount',
    'missingReferenceRelationshipWorkflowCount',
    'missingReferenceSpouseIdCount',
    'missingReferenceParentIdsCount',
    'missingReferenceOtherSourceCount',
  ] as const);

export type TreeRuntimeSelectorDiscrepancySourceKey =
  typeof TREE_RUNTIME_SELECTOR_DISCREPANCY_SOURCE_KEYS[number];

export type TreeRuntimeSelectorDiscrepancySourceClassification =
  Readonly<
    Record<
      TreeRuntimeSelectorDiscrepancySourceKey,
      number
    >
  >;

export interface TreeRuntimeSelectorDiscrepancySourceInput {
  readonly canonicalProjections:
    ReadonlyMap<
      string,
      CanonicalViewerMemberProjection
    >;

  readonly topologyMembers:
    readonly CanonicalTreeTopologyReplacementMemberResult[];

  readonly canonicalEdges:
    readonly CanonicalTreeRelationshipEdge[];

  readonly unresolvedCompatibilityEvidence:
    readonly CanonicalCompatibilityEvidence[];
}

type MutableCounts =
  Record<
    TreeRuntimeSelectorDiscrepancySourceKey,
    number
  >;

type EdgeSource =
  CanonicalTreeRelationshipEdge['source'];

const createMutableCounts =
  (): MutableCounts =>
    Object.fromEntries(
      TREE_RUNTIME_SELECTOR_DISCREPANCY_SOURCE_KEYS.map(
        key => [key, 0],
      ),
    ) as MutableCounts;

const terminalEdgeSource =
  (
    projection:
      CanonicalViewerMemberProjection | undefined,

    edgeSourceById:
      ReadonlyMap<string, EdgeSource>,
  ): EdgeSource | 'NO-TERMINAL-EDGE' | 'UNKNOWN' => {
    const terminalStep =
      projection?.connectionPath.at(-1);

    if (!terminalStep) {
      return 'NO-TERMINAL-EDGE';
    }

    return (
      edgeSourceById.get(
        terminalStep.edgeId,
      ) ??
      'UNKNOWN'
    );
  };

const incrementTerminalSource =
  (
    prefix:
      | 'canonicalAddition'
      | 'legacyGenerationMissing',

    source:
      EdgeSource |
      'NO-TERMINAL-EDGE' |
      'UNKNOWN',

    counts:
      MutableCounts,
  ): void => {
    const key =
      source === 'canonical-edge'
        ? `${prefix}CanonicalEdgeCount`
        : source === 'relationship_history'
          ? `${prefix}RelationshipHistoryCount`
          : source === 'spouse_partner_edges'
            ? `${prefix}SpousePartnerEdgesCount`
            : source === 'relationship_workflow'
              ? `${prefix}RelationshipWorkflowCount`
              : source === 'spouse_id'
                ? `${prefix}SpouseIdCount`
                : source === 'parent_ids'
                  ? `${prefix}ParentIdsCount`
                  : source === 'NO-TERMINAL-EDGE'
                    ? `${prefix}NoTerminalEdgeCount`
                    : `${prefix}UnknownEdgeSourceCount`;

    counts[
      key as TreeRuntimeSelectorDiscrepancySourceKey
    ] += 1;
  };

const incrementMissingReferenceSource =
  (
    source:
      CanonicalCompatibilityEvidence['source'],

    counts:
      MutableCounts,
  ): void => {
    const key =
      source === 'canonical-edge'
        ? 'missingReferenceCanonicalEdgeCount'
        : source === 'relationship_history'
          ? 'missingReferenceRelationshipHistoryCount'
          : source === 'spouse_partner_edges'
            ? 'missingReferenceSpousePartnerEdgesCount'
            : source === 'relationship_workflow'
              ? 'missingReferenceRelationshipWorkflowCount'
              : source === 'spouse_id'
                ? 'missingReferenceSpouseIdCount'
                : source === 'parent_ids'
                  ? 'missingReferenceParentIdsCount'
                  : 'missingReferenceOtherSourceCount';

    counts[key] += 1;
  };

export const classifyTreeRuntimeSelectorDiscrepancySources =
  (
    input:
      TreeRuntimeSelectorDiscrepancySourceInput,
  ): TreeRuntimeSelectorDiscrepancySourceClassification => {
    const counts =
      createMutableCounts();

    const edgeSourceById =
      new Map(
        input.canonicalEdges.map(
          edge =>
            [
              edge.id,
              edge.source,
            ] as const,
        ),
      );

    for (
      const result of
      input.topologyMembers
    ) {
      const projection =
        input.canonicalProjections.get(
          result.memberId,
        );

      if (
        result.classification ===
        'CANONICAL-ONLY'
      ) {
        incrementTerminalSource(
          'canonicalAddition',
          terminalEdgeSource(
            projection,
            edgeSourceById,
          ),
          counts,
        );
      }

      if (
        result.classification ===
          'GENERATION-MISMATCH' &&
        result.legacyGeneration === null &&
        result.candidateGeneration !== null
      ) {
        incrementTerminalSource(
          'legacyGenerationMissing',
          terminalEdgeSource(
            projection,
            edgeSourceById,
          ),
          counts,
        );
      }
    }

    for (
      const evidence of
      input.unresolvedCompatibilityEvidence
    ) {
      if (
        !evidence.reason
          .toLowerCase()
          .includes(
            'not present in the loaded graph',
          )
      ) {
        continue;
      }

      incrementMissingReferenceSource(
        evidence.source,
        counts,
      );
    }

    return Object.freeze(counts);
  };

export const sanitizeTreeRuntimeSelectorDiscrepancySourceClassification =
  (
    candidate: unknown,
  ): TreeRuntimeSelectorDiscrepancySourceClassification | null => {
    if (
      !candidate ||
      typeof candidate !== 'object'
    ) {
      return null;
    }

    const value =
      candidate as
        Record<string, unknown>;

    const sanitized =
      createMutableCounts();

    for (
      const key of
      TREE_RUNTIME_SELECTOR_DISCREPANCY_SOURCE_KEYS
    ) {
      const count =
        value[key];

      if (
        typeof count !== 'number' ||
        !Number.isInteger(count) ||
        count < 0 ||
        count > 1_000_000
      ) {
        return null;
      }

      sanitized[key] = count;
    }

    return Object.freeze(sanitized);
  };

export const createEmptyTreeRuntimeSelectorDiscrepancySourceClassification =
  (): TreeRuntimeSelectorDiscrepancySourceClassification =>
    Object.freeze(
      createMutableCounts(),
    );
